import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  assignmentsAtom,
  assignmentsLoadingAtom,
  assignmentsErrorAtom,
  fetchAssignmentActionAtom,
  fetchAssignmentConversationsActionAtom,
  deleteAssignmentActionAtom,
} from '@/stores/assignmentsStore';
import type { AssignmentResponse } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertTriangle,
  Calendar,
  Edit,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { getStatusIcon } from '@/utils/getStatusIcon';
import { formatDate } from '@/utils/formatDate';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { AssignmentProgress } from '@/components/assignment/AssignmentProgress';

import { AssignmentTags } from '@/components/assignment/AssignmentTags';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AssignmentCardDescription } from '@/components/assignment/AssignmentCardDescription';
import { AssignmentRelatedInfo } from '@/components/assignment/AssignmentRelatedInfo';
import { AssignmentKeyQuotes } from '@/components/assignment/AssignmentKeyQuotes';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AssignmentFactualClaims } from '@/components/assignment/AssignmentFactualClaims';
import { PreInterviewWorkflow } from '@/components/assignment/workflow/PreInterviewWorkflow';
import { PostInterviewWorkflow } from '@/components/assignment/workflow/PostInterviewWorkflow';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { getPriorityColor, getStatusColor } from '@/utils/assignments';

export function AssignmentDetails() {
  const { id } = useParams<{ id: string }>();

  const assignments = useAtomValue(assignmentsAtom);
  const isLoading = useAtomValue(assignmentsLoadingAtom);
  const error = useAtomValue(assignmentsErrorAtom);
  const fetchAssignment = useSetAtom(fetchAssignmentActionAtom);
  const fetchAssignmentConversationsWithTranscripts = useSetAtom(
    fetchAssignmentConversationsActionAtom
  );
  const deleteAssignment = useSetAtom(deleteAssignmentActionAtom);

  const [assignment, setAssignment] = useState<AssignmentResponse | null>(null);
  const [activeTab, setActiveTab] = useState('KeyQuotes');

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;

    const existing = assignments.find(a => a.id === id);
    if (existing) {
      setAssignment(existing);
    } else {
      fetchAssignment(id);
    }
  }, [
    id,
    assignments,
    fetchAssignment,
    fetchAssignmentConversationsWithTranscripts,
  ]);

  if (isLoading && !assignment) {
    return <div className='p-4'>Loading...</div>;
  }

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await deleteAssignment(assignmentId);
      navigate('/assignments');
      toast.success('Assignment deleted successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete assignment';
      toast.error(errorMessage);
    }
  };

  if (error || (!isLoading && !assignment)) {
    return (
      <div className='p-4'>
        <Alert variant='destructive'>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>
            {error || 'Assignment not found'}
            <div className='mt-2'>
              <Button asChild variant='outline' size='sm'>
                <RouterLink to='/assignments'>Back to assignments</RouterLink>
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!assignment) return null;

  return (
    <div className='flex h-screen flex-col p-3'>
      <div className='mb-4 flex justify-between items-center'>
        <Button asChild variant='outline' size='sm'>
          <RouterLink to='/assignments'>Back</RouterLink>
        </Button>
      </div>

      <div className='flex-1 min-h-0 overflow-hidden'>
        <ScrollArea className='h-full'>
          <Card>
            <CardHeader className='pb-3'>
              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  <div className='flex justify-between'>
                    <div className='flex items-center gap-2 mb-2'>
                      {getStatusIcon(assignment.status)}
                      <Badge className={getStatusColor(assignment.status)}>
                        {assignment.status.replace('_', ' ')}
                      </Badge>
                      <Badge
                        variant={getPriorityColor(assignment.priority) as any}
                      >
                        {assignment.priority}
                      </Badge>
                      <span className='text-sm text-muted-foreground'>
                        Created {formatDate(assignment.createdAt)}
                      </span>
                    </div>

                    {/* edit/delete dropdown */}
                    <div className='flex items-start gap-2'>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='shrink-0'
                          >
                            <MoreHorizontal className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align='end'
                          className='bg-popover text-popover-foreground border border-border rounded-md shadow-md p-1 min-w-[180px]'
                        >
                          <DropdownMenuItem
                            asChild
                            className='flex items-center gap-2 px-3 py-2 text-sm cursor-pointer'
                          >
                            <RouterLink
                              to={`/assignments/${assignment.id}/edit`}
                            >
                              <Edit className='h-4 w-4' />
                              <span>Edit Assignment</span>
                            </RouterLink>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() =>
                              handleDeleteAssignment(assignment.id)
                            }
                            className='flex items-center gap-2 px-3 py-2 text-sm text-destructive cursor-pointer'
                          >
                            <Trash2 className='h-4 w-4' />
                            <span>Delete Assignment</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <CardTitle className='text-lg mb-1 text-left'>
                    {assignment.title}
                  </CardTitle>

                  {assignment.description && (
                    <AssignmentCardDescription
                      description={assignment.description}
                    />
                  )}

                  <AssignmentRelatedInfo
                    assignment={assignment}
                    className='mb-3'
                  />

                  {assignment.deadline && (
                    <div className='flex items-center gap-1 text-sm text-muted-foreground mb-3'>
                      <Calendar className='h-3 w-3' />
                      <span>Due: {formatDate(assignment.deadline)}</span>
                    </div>
                  )}
                  <div className='justify-self-start'>
                    <AssignmentTags assignment={assignment} />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <AssignmentProgress assignment={assignment} />

              <div className='space-y-4'>
                <PreInterviewWorkflow assignment={assignment} />
                <PostInterviewWorkflow assignment={assignment} isDetailsPage />
              </div>

              <Separator />
              {/* Content Viewer TODO: Rearrange*/}
              {/* <ContentViewer assignment={assignment} /> */}

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className='mb-4 w-full'
              >
                <TabsList className='w-full'>
                  <TabsTrigger value='KeyQuotes'>Key Quotes</TabsTrigger>
                  <TabsTrigger value='FactualClaims'>
                    Factual Claims
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {activeTab === 'KeyQuotes' ? (
                <AssignmentKeyQuotes quotes={assignment.keyQuotes} />
              ) : (
                <AssignmentFactualClaims claims={assignment.factualClaims} />
              )}
            </CardContent>
          </Card>
        </ScrollArea>
      </div>
    </div>
  );
}
